import Fuse from 'fuse.js'
import { IPositionable, ItemBlock, ItemBlockDiscountType, ItemBlockType, ItemsBlock, ItemsSelectionRequirementType, ItemsSelectionType, SearchableContentBlock, Signature } from "../types"
import i18n from '../translations'

interface Summary {
	subtotal: number
	subtotalRecurring: number
	tax: number
	taxRecurring: number
	discount: number
	discountRecurring: number
	nettotal: number
	nettotalRecurring: number
	total: number
	totalRecurring: number
}



export default class ContentBlockHelper {
	static POSITION_STEP_INDEX = 16384

	static getCreatePosition(resources: IPositionable[]): number {
		if (resources.length === 0) return this.POSITION_STEP_INDEX

		return resources[0].position / 2
	}

	static getCreatePositionFromIndex<T extends IPositionable>(items: T[], index: number): number {
		if (items.length === 0) return this.POSITION_STEP_INDEX

		const indexContentBlock = items[index]
		const destinationContentBlock = items[index + 1]

		if (destinationContentBlock) { // Moved in between
			return (indexContentBlock.position + destinationContentBlock.position) / 2
		} else { // Moved to last position
			return indexContentBlock.position + this.POSITION_STEP_INDEX
		}
	}

	static getSignedSignatures(signatures: Signature[]): Signature[] {
		if (!signatures) return []

		return signatures.filter(signature => Boolean(signature.signed_at))
	}

	static getUnsignedSignatures(signatures: Signature[]): Signature[] {
		if (!signatures) return []

		return signatures.filter(signature => !Boolean(signature.signed_at))
	}

	static getUnsignedUserSignature(signatures: Signature[], signKey?: string): Signature | null {
		if (!signatures || !signKey) return null

		return signatures.find(signature => signature.key === signKey && !Boolean(signature.signed_at))
	}

	static getItemSummary(item: ItemBlock): Summary {
		let subtotal = 0
		let subtotalRecurring = 0
		let discountTotal = 0
		let discountTotalRecurring = 0
		let netTotal = 0
		let netTotalRecurring = 0
		let taxTotal = 0
		let taxRecurringTotal = 0
		let total = 0
		let totalRecurring = 0

		if (item.quantity && item.amount) {
			subtotal = Number(item.quantity) * Number(item.amount)

			if (item.type === ItemBlockType.RECURRING) {
				subtotalRecurring = subtotal
			}
		}

		if (item.discount > 0) {
			if (item.discount_type === ItemBlockDiscountType.PERCENTAGE) {
				discountTotal = subtotal * Number(item.discount)
			} else {
				discountTotal = Number(item.discount)
			}

			if (item.type === ItemBlockType.RECURRING) {
				discountTotalRecurring = discountTotal
			}
		}

		netTotal = subtotal - discountTotal

		if (item.type === ItemBlockType.RECURRING) {
			netTotalRecurring = netTotal
		}

		if (item.tax_rate) {
			taxTotal += netTotal * Number(item.tax_rate)

			if (item.type === ItemBlockType.RECURRING) {
				taxRecurringTotal += taxTotal
			}
		}

		total = netTotal + taxTotal

		if (item.type === ItemBlockType.RECURRING) {
			totalRecurring = total
		}

		return {
			subtotal: subtotal,
			subtotalRecurring: subtotalRecurring,
			discount: discountTotal,
			discountRecurring: discountTotalRecurring,
			nettotal: netTotal,
			nettotalRecurring: netTotalRecurring,
			tax: taxTotal,
			taxRecurring: taxRecurringTotal,
			total: total,
			totalRecurring: totalRecurring
		}
	}

	static getItemsBlockSummary(itemsBlock: ItemsBlock): Summary {
		const selectedItems = itemsBlock.contact_selection_enabled ? itemsBlock.items.filter(item => item.selected) : itemsBlock.items

		const subtotal = selectedItems.reduce((subtotal, item) => subtotal + this.getItemSummary(item).subtotal, 0)
		const subtotalRecurring = selectedItems.filter(item => item.type === ItemBlockType.RECURRING).reduce((subtotal, item) => subtotal + this.getItemSummary(item).subtotalRecurring, 0)
		const discount = selectedItems.reduce((discount, item) => discount + this.getItemSummary(item).discount, 0)
		const discountRecurring = selectedItems.filter(item => item.type === ItemBlockType.RECURRING).reduce((discountRecurring, item) => discountRecurring + this.getItemSummary(item).discountRecurring, 0)
		const netTotal = selectedItems.reduce((netTotal, item) => netTotal + this.getItemSummary(item).nettotal, 0)
		const netTotalRecurring = selectedItems.filter(item => item.type === ItemBlockType.RECURRING).reduce((netTotalRecurring, item) => netTotalRecurring + this.getItemSummary(item).nettotalRecurring, 0)
		const tax = selectedItems.reduce((tax, item) => tax + this.getItemSummary(item).tax, 0)
		const taxRecurring = selectedItems.filter(item => item.type === ItemBlockType.RECURRING).reduce((taxRecurring, item) => taxRecurring + this.getItemSummary(item).taxRecurring, 0)
		const total = selectedItems.reduce((total, item) => total + this.getItemSummary(item).total, 0)
		const totalRecurring = selectedItems.reduce((total, item) => total + this.getItemSummary(item).totalRecurring, 0)


		return {
			subtotal: subtotal,
			subtotalRecurring: subtotalRecurring,
			discount: discount,
			discountRecurring: discountRecurring,
			tax: tax,
			taxRecurring: taxRecurring,
			nettotal: netTotal,
			nettotalRecurring: netTotalRecurring,
			total: total,
			totalRecurring: totalRecurring
		}
	}

	static getSummary(items: ItemsBlock[]): Summary {
		return {
			subtotal: items.reduce((subtotal, item) => subtotal + this.getItemsBlockSummary(item).subtotal, 0),
			subtotalRecurring: items.reduce((subtotalRecurring, item) => subtotalRecurring + this.getItemsBlockSummary(item).subtotalRecurring, 0),
			tax: items.reduce((tax, item) => tax + this.getItemsBlockSummary(item).tax, 0),
			taxRecurring: items.reduce((taxRecurring, item) => taxRecurring + this.getItemsBlockSummary(item).taxRecurring, 0),
			discount: items.reduce((discount, item) => discount + this.getItemsBlockSummary(item).discount, 0),
			discountRecurring: items.reduce((discountRecurring, item) => discountRecurring + this.getItemsBlockSummary(item).discountRecurring, 0),
			nettotal: items.reduce((nettotal, item) => nettotal + this.getItemsBlockSummary(item).nettotal, 0),
			nettotalRecurring: items.reduce((nettotalRecurring, item) => nettotalRecurring + this.getItemsBlockSummary(item).nettotalRecurring, 0),
			total: items.reduce((total, item) => total + this.getItemsBlockSummary(item).total, 0),
			totalRecurring: items.reduce((totalRecurring, item) => totalRecurring + this.getItemsBlockSummary(item).totalRecurring, 0)
		}
	}

	static getSelectionChangeItems(itemsBlock: ItemsBlock, itemIndex: number): ItemBlock[] {
		const item = itemsBlock.items[itemIndex]
		const newItemBlockSelected = !item.selected

		if (itemsBlock.selection_type === ItemsSelectionType.SINGLE) {
			if (newItemBlockSelected) {
				let newItems = itemsBlock.items.map(item => ({ ...item, selected: false }))

				newItems[itemIndex] = { ...item, selected: newItemBlockSelected }

				return newItems
			} else {
				if (itemsBlock.selection_requirement_type === ItemsSelectionRequirementType.REQUIRED) {
					return [...itemsBlock.items]
				} else if (itemsBlock.selection_requirement_type === ItemsSelectionRequirementType.OPTIONAL) {
					let newItems = [...itemsBlock.items]

					newItems[itemIndex] = { ...item, selected: newItemBlockSelected }

					return newItems
				}
			}
		} else if (itemsBlock.selection_type === ItemsSelectionType.MULTI) {
			if (newItemBlockSelected) {
				let newItems = [...itemsBlock.items]

				newItems[itemIndex] = { ...item, selected: newItemBlockSelected }

				return newItems
			} else {
				if (itemsBlock.selection_requirement_type === ItemsSelectionRequirementType.REQUIRED) {
					const selectedItemsCount = itemsBlock.items.filter(item => item.selected).length

					if (selectedItemsCount === 1) {
						return [...itemsBlock.items]
					} else {
						let newItems = [...itemsBlock.items]

						newItems[itemIndex] = { ...item, selected: newItemBlockSelected }

						return newItems
					}
				} else if (itemsBlock.selection_requirement_type === ItemsSelectionRequirementType.OPTIONAL) {
					let newItems = [...itemsBlock.items]

					newItems[itemIndex] = { ...item, selected: newItemBlockSelected }

					return newItems
				}
			}
		}
	}

	static getContentBlocksFromSearch(searchValue: string, searchableContentBlocks: SearchableContentBlock[]): SearchableContentBlock[] {
		const fuse = new Fuse(searchableContentBlocks, {
			shouldSort: true,
			threshold: 0.5,
			location: 0,
			distance: 100,
			minMatchCharLength: 1,
			keys: [
				'name',
				'notes',
				'amount',
				'contact.name',
				'project.name',
				'assignee.email',
				'assignee.first_name',
				'assignee.last_name'
			]
		})

		if (searchValue.length > 0) {
			return fuse.search(searchValue).map(result => result.item)
		}

		return searchableContentBlocks
	}

	static getItemTypeBlockOptions() {
		return Object.keys(ItemBlockType).map(key => (
			{ label: i18n.t(`ItemBlockType::${ItemBlockType[key]}`), value: ItemBlockType[key] }
		))
	}
}